/* eslint-disable indent */
import {
  prop,
  includes,
  pipe,
  all,
  __,
  pathEq,
  allPass,
  or,
  ifElse,
  always,
  path,
  isEmpty
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'

import { isCartValid } from '../../../../utils/cart'
import { hasMobilityActivationInCart } from '../../../../utils/mobility'

const {
  getTopLevelItems,
  isBusinessCart,
  hasOnlyMobilityAccessories,
  _getItems,
  isHSEppCart
} = utils.cart

const isConsumerCart = pathEq(['root', 'market', 'type'], 'PERSONAL')

const hasValidFlowForEmailCart = (cart) => {
  const consumerFlow = isConsumerCart(cart)
  const sbsFlow = isBusinessCart(cart)

  return or(consumerFlow, sbsFlow)
}

const hasEligibleOffers = (cart) => {
  const validCategories = [
    'MOBILITY_SIM',
    'MOBILITY_PHONE',
    'MOBILITY_TABLET',
    'MOBILITY_OFFER',
    'MOBILITY_PLAN',
    'MOBILITY_ACCESSORY',
    'MOBILITY_WARRANTY',
    'MOBILITY_CONNECTION_FEE',
    'MOBILITY_SHIPPING'
  ]

  return pipe(getTopLevelItems, all(pipe(prop('category'), includes(__, validCategories))))(cart)
}

const hasValidAccessories = (cart) => {
  const hasOnlyAccessories = hasOnlyMobilityAccessories(cart)
  if (hasOnlyAccessories) return true

  const validCategoriesForAccessories = [
    'MOBILITY_ACCESSORY',
    'MOBILITY_SHIPPING',
    'MOBILITY_OFFER'
  ]

  return pipe(
    _getItems,
    all(pipe(prop('category'), includes(__, validCategoriesForAccessories)))
  )(cart)
}

const isEmailYourCartEligible = (cart) => {
  if (!isCartValid(cart) || isEmpty(prop('items', cart))) {
    return false
  }

  const cartHasValidAccessories = hasValidAccessories(cart)
  if (cartHasValidAccessories) return true

  return allPass([hasMobilityActivationInCart, hasEligibleOffers, hasValidFlowForEmailCart])(cart)
}

const isOneTimePromocode = (promocode) => !!(promocode && promocode.includes('.'))

const isCartMobilityAndHs = pathEq(['root', 'cartType'], 'MIXED')
const isHSCart = pathEq(['root', 'cartType'], 'HS')
const isSHS = pipe(
  prop('tags'),
  allPass([
    includes('service-plan'),
    includes('security'),
    ifElse(includes('smartwear'), always(false), always(true)),
    ifElse(includes('smartwear-device'), always(false), always(true))
  ])
)
const isConsumerType = (cart) => cart?.root?.customerType === 'CONSUMER'
const hasProvisioner = (cart, provisioner) => {
  return pipe(path(['root', 'provisioners']), includes(provisioner))(cart)
}

const getEmailYourCartPhase3FlagsStatus = ({ offersTags, featureFlags }) => {
  if (!featureFlags) return false
  const toggleValues = offersTags.map((tag) => {
    switch (tag) {
      case 'internet':
        return featureFlags.internetFifaEyc3
      case 'optik':
        return featureFlags.optikTvEyc3
      case 'homephone':
        return featureFlags.homePhoneEyc3
      case 'smart-hub':
        return featureFlags.smartHubEyc3
      case 'tos-standalone':
        return featureFlags.tosEyc3
      case 'security':
        return featureFlags.shsEyc3
      case 'lwc':
        return featureFlags.lwcEyc3
      case 'smartwear':
        return featureFlags.swsEyc3
      default:
        return false
    }
  })
  return !toggleValues.includes(false)
}
const _filterOfferCategory = (offerCategory, featureFlags) => {
  if (
    featureFlags?.smartHubEyc3 === true &&
    offerCategory.includes('internet') &&
    offerCategory.includes('smart-hub')
  )
    return offerCategory.filter((tag) => tag !== 'internet')

  if (
    featureFlags?.swsEyc3 === true &&
    offerCategory.includes('security') &&
    offerCategory.includes('smartwear')
  )
    return offerCategory.filter((tag) => tag !== 'security')

  return offerCategory
}

const getCartOffersTags = (cart, featureFlags) => {
  let filteredTags = []

  Object.entries(cart.items).forEach((offer) => {
    const tags = path([1, 'tags'], offer)

    const isMainOffering = tags.includes('service-plan')

    const offerCategory = tags.filter((tag) => {
      if (isMainOffering) {
        return [
          'tos-standalone',
          'optik',
          'internet',
          'homephone',
          'smart-hub',
          'security',
          'lwc',
          'smartwear'
        ].includes(tag)
      }
      if (tag === 'tos-standalone') {
        filteredTags.push('tos-standalone')
      }
      return false
    })

    const filteredOfferCategory = _filterOfferCategory(offerCategory, featureFlags)

    if (filteredOfferCategory.length > 0) {
      filteredTags = Object.values(filteredOfferCategory)
    } else if (isMainOffering) {
      filteredTags.push('not-eyc-offer')
    }
  })
  return filteredTags
}

export const checkShouldRenderEmailYourCart = (cart, cookies, isAuthenticated, featureFlags) => {
  const isSBSCart = isBusinessCart(cart)
  const isMHElegible = isCartMobilityAndHs(cart) && featureFlags.enableEmailYourCartForMh === true
  const isHSElegible = isHSCart(cart) && featureFlags.enableEmailYourCartForHs === true
  const hasShsItem = Object.values(cart.items).find((item) => isSHS(item))
  const isSbsShsElegible = hasShsItem ? featureFlags.sbsShsEyc3 : true

  const isSbsElegible = isSBSCart && (isMHElegible || isHSElegible) && isSbsShsElegible
  const offersTags = getCartOffersTags(cart, featureFlags)
  const emailYourCartPhase3FlagStatus =
    hasProvisioner(cart, 'FIFA') &&
    getEmailYourCartPhase3FlagsStatus({
      offersTags,
      featureFlags
    })
  const isMnHEnabled = cart.root.cartType === 'MIXED' ? featureFlags.consumerMnHEyc3 : true
  const shouldDisplayEmailYourCartCTA =
    isSbsElegible || (isConsumerType(cart) && emailYourCartPhase3FlagStatus && isMnHEnabled)

  return (
    !isHSEppCart(cart) &&
    (isEmailYourCartEligible(cart) || shouldDisplayEmailYourCartCTA) &&
    !isOneTimePromocode(cookies.commercePromoCodeLog) &&
    !isAuthenticated
  )
}

const getAgentToken = () => sessionStorage.getItem('agentToken')

export const isCartCreatedByAgent = (cart) => {
  return !!getAgentToken() && isBusinessCart(cart)
}

export const getEmailYourCartLink = (cart, lang = 'en') => {
  const sufix = isBusinessCart(cart) ? 'business/' : ''
  return `/${lang}/${sufix}commerce/cart/save-your-cart`
}
